// src/pages/EventsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  useAuthenticator,
  Flex,
  Heading,
  Loader,
  SelectField,
  SwitchField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  View,
} from "@aws-amplify/ui-react";
import * as queries from "../graphql/queries";
import { gql } from "../lib/gqlClient";
import { useAdminTenant } from "../admin/AdminTenantContext";
import AdminTenantRequired from "../components/admin/AdminTenantRequired";
import { getEventTypeMeta, getEventTypeOptions } from "../utils/schema/enumUtils.js";
import { useSearchParams } from "react-router-dom";

function formatTs(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function buildLocation(device) {
  if (!device) return "";
  const parts = [
    device.propertyCode,
    device.buildingCode,
    device.unitCode,
    device.room,
  ].filter(Boolean);
  return parts.join(" · ");
}

function minIsoForRange(timeFilter) {
  const now = Date.now();
  let minMs = 0;

  if (timeFilter === "24h") minMs = now - 24 * 60 * 60 * 1000;
  else if (timeFilter === "7d") minMs = now - 7 * 24 * 60 * 60 * 1000;
  else if (timeFilter === "30d") minMs = now - 30 * 24 * 60 * 60 * 1000;

  return minMs > 0 ? new Date(minMs).toISOString() : null;
}

function buildEventsQuery({ tenantKey, timeFilter, deviceIdFilter, eventTypeFilter, nextToken }) {
  const minIso = minIsoForRange(timeFilter);
  const hasDevice = !!deviceIdFilter;
  const hasType = eventTypeFilter && eventTypeFilter !== "ALL";
  const maxIso = "9999-12-31T23:59:59.999Z";

  // Decide query + variables
  if (hasDevice && hasType) {
    return {
      query: queries.eventsByTenantDeviceTypeTime,
      rootKey: "eventsByTenantDeviceTypeTime",
      variables: {
        tenantKey,
        sortDirection: "DESC",
        limit: 50,
        nextToken,
        deviceIdTypeTs: minIso 
          ? {
            between: [
              { deviceId: deviceIdFilter, type: eventTypeFilter, ts: minIso },
              { deviceId: deviceIdFilter, type: eventTypeFilter, ts: maxIso },
            ]
          }
          : { beginsWith: { deviceId: deviceIdFilter, type: eventTypeFilter } }
      },
    };
  }

  if (hasDevice && !hasType) {
    return {
      query: queries.eventsByTenantDeviceTime,
      rootKey: "eventsByTenantDeviceTime",
      variables: {
        tenantKey,
        sortDirection: "DESC",
        limit: 50,
        nextToken,
        deviceIdTs: minIso 
          ? {
            between: [
              { deviceId: deviceIdFilter, ts: minIso },
              { deviceId: deviceIdFilter, ts: maxIso },
            ]
          }
          : { beginsWith: { deviceId: deviceIdFilter } }
      }
    };
  }

  if (!hasDevice && hasType) {
    return {
      query: queries.eventsByTenantTypeTime,
      rootKey: "eventsByTenantTypeTime",
      variables: {
        tenantKey,
        sortDirection: "DESC",
        limit: 50,
        nextToken,
        typeTs: minIso 
          ? { between: [
            { type: eventTypeFilter, ts: minIso },
            { type: eventTypeFilter, ts: maxIso }
          ]} 
          : { beginsWith: { type: eventTypeFilter } }
      },
    };
  }

  // Default: all events by tenant + time
  return {
    query: queries.eventsByTenantTime,
    rootKey: "eventsByTenantTime",
    variables: {
      tenantKey,
      sortDirection: "DESC",
      limit: 50,
      nextToken,
      ...(minIso ? { ts: { ge: minIso } } : {}),
    },
  };
}

export default function EventsPage() {
  const navigate = useNavigate();
  const { tenantKey, isPlatformAdmin } = useAdminTenant();
  const [events, setEvents] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");

  const [devicesById, setDevicesById] = useState({});
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState("");

  const [timeFilter, setTimeFilter] = useState("7d"); // "24h" | "7d" | "30d" | "all"
  const [loadingMore, setLoadingMore] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState("ALL"); 

  const [searchParams, setSearchParams] = useSearchParams();
  const deviceIdFilter = (searchParams.get("deviceId") || "").trim();

  const allEventTypeOptions = useMemo(() => getEventTypeOptions(), []);

  // Keep the dropdown focused on the types you actually care about day-to-day.
  // Everything else is still visible under "All types".
  const filterableTypes = useMemo(() => {
    const allow = new Set([
      "LEAK_DETECTED",
      "LEAK_CLEARED",
      "LOW_BATTERY",
      "LOW_BATTERY_CLEARED",
      "DEVICE_TILTED",
      "DEVICE_TILT_CLEARED",
      "DEVICE_DETECTED_OFFLINE",
      "DEVICE_BACK_ONLINE",
      // Optional: useful operational events
      "DEVICE_REBOOTED",
      "DEVICE_FIRST_ONLINE",
    ]);

    return allEventTypeOptions.filter((opt) => allow.has(opt.value));
  }, [allEventTypeOptions]);

  // Initial load of events
  useEffect(() => {
    if (!tenantKey) return;

    let cancelled = false;
    (async () => {
      setEventsLoading(true);
      setEventsError("");
      setNextToken(null);
      setEvents([]);
      try {
        const { query, variables, rootKey } = buildEventsQuery({
          tenantKey,
          timeFilter,
          deviceIdFilter,
          eventTypeFilter,
          nextToken: null,
        });

        const data = await gql(query, variables);
        if (cancelled) return;

        const items = data?.[rootKey]?.items ?? [];
        setEvents(items);
        setNextToken(data?.[rootKey]?.nextToken ?? null);
      } catch (err) {
        console.error("Failed to load events", err);
        if (!cancelled) {
          setEventsError("Failed to load recent events.");
        }
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantKey, timeFilter, deviceIdFilter, eventTypeFilter]);

  // Load devices once so we can show labels and locations
  useEffect(() => {
    if (!tenantKey) return;

    let cancelled = false;
    (async () => {
      setDevicesLoading(true);
      setDevicesError("");
      try {
        const data = await gql(queries.devicesByTenantCreatedAt, {
          tenantKey,
          sortDirection: "ASC",
          limit: 1000, // adjust as needed
        });

        if (cancelled) return;

        const items = data?.devicesByTenantCreatedAt?.items ?? [];
        const map = {};
        for (const d of items) {
          if (d?.id) map[d.id] = d;
        }
        setDevicesById(map);
      } catch (err) {
        console.error("Failed to load devices for event enrichment", err);
        if (!cancelled) {
          setDevicesError("Failed to load device details for events.");
        }
      } finally {
        if (!cancelled) setDevicesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantKey]);

  async function handleLoadMore() {
    if (!tenantKey || !nextToken) return;
    setLoadingMore(true);
    try {
        const { query, variables, rootKey } = buildEventsQuery({
        tenantKey,
        timeFilter,
        deviceIdFilter,
        eventTypeFilter,
        nextToken,
      });

      const data = await gql(query, variables);

      const items = data?.[rootKey]?.items ?? [];
      setEvents((prev) => [...prev, ...items]);
      setNextToken(data?.[rootKey]?.nextToken ?? null);
    } catch (err) {
      console.error("Failed to load more events", err);
      setEventsError("Failed to load more events.");
    } finally {
      setLoadingMore(false);
    }
  }

  const anyLoading = eventsLoading || devicesLoading;

  if (isPlatformAdmin && !tenantKey) {
    return (
      <AdminTenantRequired
        title="View Events"
        message="Select a tenant in the top bar before viewing events."
      />
    );
  }

  return (
    <View
      maxWidth="64rem"
      margin="0 auto"
      padding={{ base: "1rem", medium: "1.5rem" }}
    >
      <Heading level={3} marginBottom="0.5rem">
        Events &amp; Alerts
      </Heading>
      <Text variation="tertiary" fontSize="small" marginBottom="1rem">
        View recent leak alerts, low-battery warnings, connectivity issues, and
        other device activity for this account.
      </Text>

      {eventsError && (
        <Alert
          variation="error"
          hasIcon
          isDismissible={true}
          onDismiss={() => setEventsError("")}
          marginBottom="0.75rem"
        >
          {eventsError}
        </Alert>
      )}

      {devicesError && (
        <Alert
          variation="warning"
          hasIcon
          isDismissible={true}
          onDismiss={() => setDevicesError("")}
          marginBottom="0.75rem"
        >
          {devicesError}
        </Alert>
      )}

      {!tenantKey && (
        <Alert variation="error" marginBottom="1rem">
          Missing tenantKey; ensure the signed-in user has custom:tenantKey2.
        </Alert>
      )}

      <Flex
        direction={{ base: "column", medium: "row" }}
        alignItems={{ base: "stretch", medium: "center" }}
        justifyContent="space-between"
        gap="0.75rem"
        marginBottom="0.75rem"
      >
        <Flex direction="column" gap="0.25rem">
          <Text fontSize="small">
            Showing {events.length} events.
          </Text>
        </Flex>

        {deviceIdFilter && (
          <Flex alignItems="center" gap="0.5rem">
            <Badge variation="info">Device</Badge>
            <Text fontSize="small">{deviceIdFilter}</Text>
            <Button
              size="small"
              variation="link"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete("deviceId");
                setSearchParams(next, { replace: true });
              }}
            >
              Clear
            </Button>
          </Flex>
        )}

        <Flex gap="0.75rem" alignItems="center">
          <SelectField
            label="Time range"
            size="small"
            labelHidden
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All</option>
          </SelectField>

          <SelectField
            label="Event type"
            size="small"
            labelHidden
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
          >
            <option value="ALL">All types</option>
            {filterableTypes.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SelectField>
        </Flex>
      </Flex>

      <View borderRadius="0.75rem" overflow="hidden" border="1px solid #e5e7eb">
        {anyLoading && !events.length ? (
          <Flex justifyContent="center" padding="1.5rem">
            <Loader />
          </Flex>
        ) : events.length === 0 ? (
          <Flex direction="column" alignItems="center" padding="1.5rem">
            <Text>No events match the current filters.</Text>
            {events.length > 0 && (
              <Text fontSize="0.75rem" variation="tertiary" marginTop="0.25rem">
                Try expanding the time range or turning off “Alerts only”.
              </Text>
            )}
          </Flex>
        ) : (
          <>
            <View display={{ base: "none", medium: "block" }}>
              <Table highlightOnHover={true} size="small"  style={{ minWidth: "720px" }}>
                <TableHead>
                  <TableRow>
                    <TableCell as="th">Time</TableCell>
                    <TableCell as="th">Type</TableCell>
                    <TableCell as="th">Device</TableCell>
                    <TableCell as="th">Location</TableCell>
                    <TableCell as="th">Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((ev) => {
                    const device = ev.deviceId ? devicesById[ev.deviceId] : null;
                    const meta = getEventTypeMeta(ev.type) || {
                      label: ev.type || "Unknown",
                      severity: "info",
                    };

                    const deviceLabel =
                      device?.label || device?.id || ev.deviceId || "—";
                    const location = buildLocation(device);

                    return (
                      <TableRow key={ev.id}>
                        <TableCell>{formatTs(ev.ts)}</TableCell>
                        <TableCell>
                          <Badge variation={meta.severity}>
                            {meta.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {ev.deviceId ? (
                            <Button
                              variation="link"
                              size="small"
                              padding="0"
                              onClick={() => navigate(`/devices/${ev.deviceId}`)}
                            >
                              <Text fontWeight={600}>{deviceLabel}</Text>
                            </Button>
                          ) : (
                            <Text fontWeight={600}>{deviceLabel}</Text>
                          )}
                          {device?.id && device?.label && (
                            <Text
                              variation="tertiary"
                              fontSize="0.75rem"
                              as="div"
                            >
                              {device.id}
                            </Text>
                          )}
                        </TableCell>
                        <TableCell>
                          {location ? (
                            <Text fontSize="0.85rem">{location}</Text>
                          ) : (
                            <Text variation="tertiary" fontSize="0.8rem">
                              —
                            </Text>
                          )}
                        </TableCell>
                        <TableCell>
                          <Text fontSize="0.9rem">
                            {ev.message || "(No message provided)"}
                          </Text>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </View>
            {/* Mobile: cards */}
            <Flex
              display={{ base: "flex", medium: "none" }}
              direction="column"
              gap="0.75rem"
              padding="0.75rem"
            >
              {events.map((ev) => {
                const device = ev.deviceId ? devicesById[ev.deviceId] : null;
                const meta = getEventTypeMeta(ev.type);
                const deviceLabel =
                  device?.label || device?.id || ev.deviceId || "—";
                const location = buildLocation(device);

                return (
                  <View
                    key={ev.id}
                    padding="0.85rem 1rem"
                    border="1px solid var(--amplify-colors-neutral-20)"
                    borderRadius="0.75rem"
                    width="100%"
                  >
                    <Flex
                      justifyContent={{ base: "flex-start", medium: "space-between" }}
                      alignItems={{ base: "flex-start", medium: "center" }}
                      direction={{ base: "column", medium: "row" }}
                      gap="0.25rem"
                      marginBottom="0.35rem"
                    >
                      <Badge variation={meta.severity}>{meta.label}</Badge>
                      <Text fontSize="0.75rem" variation="tertiary">
                        {formatTs(ev.ts)}
                      </Text>
                    </Flex>

                    <Button
                      variation="link"
                      size="small"
                      padding="0"
                      onClick={() => navigate(`/devices/${ev.deviceId}`)}
                    >
                      <Text fontWeight={600}>{deviceLabel}</Text>
                    </Button>
                    {device?.label && (
                      <Text variation="tertiary" fontSize="0.75rem">
                        {device.id}
                      </Text>
                    )}

                    {location && (
                      <Text fontSize="0.8rem" marginTop="0.25rem">
                        {location}
                      </Text>
                    )}

                    {ev.message && (
                      <Text fontSize="0.85rem" marginTop="0.5rem">
                        {ev.message}
                      </Text>
                    )}
                  </View>
                );
              })}
            </Flex>
          </>
        )}
      </View>

      <Flex justifyContent="space-between" marginTop="0.75rem">

        {nextToken && (
          <Button
            size="small"
            variation="link"
            onClick={handleLoadMore}
            isLoading={loadingMore}
          >
            Load more
          </Button>
        )}
      </Flex>
    </View>
  );
}
